import type { User } from '@core/account-types';
import type { ShareOnTheGoRequestPayload } from '@core/candidate-types';
import type { InterviewResultRequestCandidatePlacement } from '@core/interview-result-request-types';
import type { JobItem } from '@core/offers-types';
import type { Stage } from '@core/stages-types';
import type { TalentPoolBrief } from '@core/talent-pools-types';

import type { InterviewKind, VideoProvider, ResponseStatus } from './interview-events.enums';

export enum InterviewSchedulerKind {
  Admin = 'Admin',
  Candidate = 'Candidate',
}

export type ResultRequest = {
  admin: User;
  adminId: number;
  candidate: any;
  candidateId: number;
  candidatePlacements: InterviewResultRequestCandidatePlacement[];
  createdAt: string;
  id: number;
  interviewEventId: number | null;
  interviewTemplate: any;
  interviewTemplateId: number;
  offer?: JobItem;
  talentPool?: TalentPoolBrief;
  offerId: number | null;
  talentPoolId: number | null;
  requestedBy: string | null;
  requesterId: number | null;
  stage?: Stage;
  stageId: number;
  status: string;
  triggered: boolean;
  dueDate?: string | null;
  dueDateType?: 'relative' | 'absolute' | null;
  dueDateUnit?: string | null;
  dueDateValue?: string | null;
  sendAtKind?: 'on_day_of_schedule' | 'on_day_of_event' | null;
};
export type EventAttendee = {
  adminId: number;
  responseStatus: ResponseStatus;
};

export type EventCandidateAttendees = {
  candidateId: number;
  responseStatus: ResponseStatus;
};

export interface ViewboxInterviewEvent {
  timezone: string;
  syncUrl: string;
  startsAt: Date;
  scheduler: InterviewSchedulerKind;
  rescheduled: boolean;
  location: string;
  language: string;
  kind: InterviewKind;
  videoProvider: VideoProvider | null;
  videoUrl: string | null;
  duration: number;
  candidate: { name: string };
  company?: { name: string };
  admins: Array<{ name: string; anonymizedAt: string }>;
  admin: { name: string; anonymizedAt: string };
  createdAt: string;
  googleCalendarUrl: string;
  yahooCalendarUrl: string;
  publicNote?: string;
  requestLinkToken: string;
  interviewMeetingRooms?: Array<{ id: number; name: string }>;
  offer: { title: string | null };
  scheduleToken: string;
  rescheduleEnabled: boolean;
  responseStatus?: ResponseStatus;
}

export type InterviewEvent = {
  admin: User;
  adminId: number;
  adminIds: number[];
  admins: User[];
  candidate: any;
  candidateEmail: string;
  candidateId: number;
  createdAt: string;
  customName: string | null;
  duration: number;
  eventAttendees: EventAttendee[];
  eventCandidateAttendees: EventCandidateAttendees[];
  eventUrl: string;
  excludeCandidateFromAttendees: boolean;
  googleCalendarUrl: string;
  id: number;
  interviewMeetingRooms: Array<{ id: number; name: string }>;
  interviewResultRequests: ResultRequest[];
  interviewResultRequestsIds: number[];
  interviewDeferredResultRequests: ResultRequest[];
  interviewDeferredResultRequestsIds: number[];
  kind: InterviewKind;
  location: string;
  note: null | string;
  notifyConfig: {
    candidateByEmail: boolean;
    candidateByCalendar: boolean;
    interviewersByEmail: boolean;
    interviewersByCalendar: boolean;
  };
  offer: JobItem;
  offerId: number | null;
  organizerId: number | null;
  oldStartsAt: string | null;
  oldTimezone: string | null;
  publicNote: string | null;
  // flag that indicate that event was rescheduled (if admin change startsAt or timezone param)
  reminders: Array<{ minutesBefore: number }>;
  rescheduled: boolean;
  rescheduledAt: string;
  rescheduledBy: string;
  reschedulerId: number;
  rescheduleReason: string | null;
  scheduled: boolean;
  scheduler: InterviewSchedulerKind | null;
  stage?: Stage;
  stageId: number;
  startsAt: string;
  syncUrl: string;
  talentPool?: TalentPoolBrief;
  talentPoolId?: number | null;
  timezone: string;
  updatedAt: string | null;
  videoProvider: VideoProvider;
  videoUrl: string;
  video: { host: string | null; hostId: number | null } | null;
  yahooCalendarUrl: string;
  hideCandidatesNames: boolean;
  private: boolean;
  responseStatus?: ResponseStatus;
};

export type InterviewEventCollection = {
  events: InterviewEvent[];
  counters: { pastDue: number; upcoming: number };
};

export type CandidateMessage = {
  to: [
    {
      candidateEmail: string | null | undefined;
      candidateId: number | undefined;
    },
  ];
  emailProviderId?: number;
  subject: string;
  bodyHtml: string;
};

export type Recipient = {
  emailProviderId?: number;
  email?: string;
  adminId?: number;
};

export type InterviewersMessage = {
  to: Recipient[];
  subject: string;
  cc: Recipient[];
  bodyHtml: string;
  bcc: Recipient[];
};

export type ScheduleEventSet = {
  candidateEmail: string;
  notifyInterviewers?: boolean | any;
  notifyCandidate?: boolean;
  notifyConfig?: {
    candidateByEmail: boolean;
    candidateByCalendar: boolean;
    interviewersByEmail: boolean;
    interviewersByCalendar: boolean;
  };
  interviewersMessage: InterviewersMessage;
  candidateMessage: CandidateMessage;
  shareOnTheGoPayload?: ShareOnTheGoRequestPayload;
  reminders: Array<{ minutesBefore: number }>;
};

export type ScheduleEvent = {
  interviewEvent: InterviewEvent;
};

export interface ScheduleSuccessData {
  interviewEvent: InterviewEvent;
  grantAccess?: ShareOnTheGoRequestPayload['grantAccess'];
}

export type DeleteEventPayload = {
  notifyCandidate: boolean;
  message: {
    to: [{ candidateEmail: string; candidateId: string }];
    bodyHtml: string;
    subject: string;
  };
};

export enum InterviewStatus {
  All = 'all',
  Upcoming = 'upcoming',
  PastDue = 'past_due',
  Today = 'today',
  ThisWeek = 'this_week',
}

export enum InterviewScope {
  My = 'my',
  All = 'all',
  Admin = '',
  Candidate = 'candidate',
}
